// src/features/manual/components/ManualSearch.tsx

import React, { useEffect } from 'react';
import { useTranslate } from 'react-admin';
import { Link as RouterLink } from 'react-router-dom';
import { alpha } from '@mui/material/styles';
import { Box, Button, IconButton, InputAdornment, LinearProgress, TextField, Typography } from '@mui/material';
import { CloseRounded as ClearIcon, Search as SearchIcon } from '@mui/icons-material';
import type { ManualSearchState } from '../hooks/useManual';
import type { ManualSearchResult } from '../types';
import { manualSectionTarget } from '../utils/labels';
import {
  highlightParts,
  isSearchable,
  isSearchShortcut,
  MANUAL_SEARCH_MAX,
  MANUAL_SEARCH_MIN,
  searchFieldKeyAction,
} from '../utils/search';
import { StaffLabel } from './StaffLabel';

/** `text` with every term marked, built as React nodes (never as HTML). */
export const HighlightedText: React.FC<{ text: string; terms: readonly string[] }> = ({ text, terms }) => (
  <>
    {highlightParts(text, terms).map((part, i) =>
      part.hit ? (
        <Box
          key={i}
          component="mark"
          sx={(theme) => ({
            p: 0,
            borderRadius: theme.custom.radius.small,
            bgcolor: alpha(theme.palette.primary.main, 0.14),
            color: 'text.primary',
            fontWeight: 600,
          })}
        >
          {part.text}
        </Box>
      ) : (
        <React.Fragment key={i}>{part.text}</React.Fragment>
      )
    )}
  </>
);

interface ManualSearchFieldProps {
  value: string;
  onChange: (value: string) => void;
  /** Enter: open the first result. */
  onSubmit: () => void;
  inputRef: React.RefObject<HTMLInputElement | null>;
}

/**
 * «Buscar en el manual». Esc empties it (and brings the index back); Enter opens the first result;
 * «/» anywhere on the page, outside another field, puts the cursor here.
 */
export const ManualSearchField: React.FC<ManualSearchFieldProps> = ({ value, onChange, onSubmit, inputRef }) => {
  const translate = useTranslate();

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (!isSearchShortcut(event)) return;
      event.preventDefault();
      inputRef.current?.focus();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [inputRef]);

  return (
    <TextField
      fullWidth
      size="small"
      type="search"
      value={value}
      onChange={(event) => onChange(event.target.value)}
      onKeyDown={(event) => {
        const action = searchFieldKeyAction(event.key, value);
        if (!action) return;
        event.preventDefault();
        if (action === 'submit') {
          onSubmit();
        } else {
          event.stopPropagation();
          onChange('');
        }
      }}
      placeholder={translate('manual.search.placeholder')}
      inputRef={inputRef}
      inputProps={{
        maxLength: MANUAL_SEARCH_MAX,
        'aria-label': translate('manual.search.label'),
        'aria-keyshortcuts': '/',
        enterKeyHint: 'search',
      }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          </InputAdornment>
        ),
        endAdornment: value ? (
          <InputAdornment position="end">
            <IconButton
              size="small"
              edge="end"
              aria-label={translate('manual.search.clear')}
              onClick={() => {
                onChange('');
                inputRef.current?.focus();
              }}
            >
              <ClearIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </InputAdornment>
        ) : null,
      }}
      sx={{
        '& .MuiOutlinedInput-root': { bgcolor: 'background.paper', minHeight: { xs: 44, lg: 36 }, fontSize: '0.875rem' },
        // The native clear cross of type="search" would double the clear button.
        '& input::-webkit-search-cancel-button': { display: 'none' },
      }}
    />
  );
};

interface ManualSearchResultsProps {
  /** What the field holds now (trimmed or not). */
  query: string;
  search: ManualSearchState;
  onOpen: (result: ManualSearchResult) => void;
}

/** Read by screen readers, not drawn. */
const visuallyHidden = { position: 'absolute', width: '1px', height: '1px', overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' } as const;

const statusSx = { px: 1.25, py: 1, fontSize: '0.8125rem', color: 'text.secondary' } as const;

/**
 * The results that replace the index while the field has text: «Capítulo › Sección», the snippet
 * with the terms marked, and the staff label on hits in staff text (only staff ever receive them).
 */
export const ManualSearchResults: React.FC<ManualSearchResultsProps> = ({ query, search, onOpen }) => {
  const translate = useTranslate();
  const trimmed = query.trim();
  const current = search.data && search.query === trimmed ? search.data : null;
  const busy = search.pending || search.isLoading;

  let status: React.ReactNode = null;
  if (trimmed.length < MANUAL_SEARCH_MIN || !isSearchable(trimmed)) {
    status = <Typography sx={statusSx}>{translate('manual.search.tooShort', { min: MANUAL_SEARCH_MIN })}</Typography>;
  } else if (search.error && !busy) {
    status = (
      <Box sx={statusSx} role="alert">
        <Typography sx={{ fontSize: 'inherit', color: 'inherit' }}>{translate('manual.search.error')}</Typography>
        <Button size="small" onClick={search.refetch} sx={{ mt: 0.5, ml: -1 }}>
          {translate('manual.search.retry')}
        </Button>
      </Box>
    );
  } else if (current && current.results.length === 0 && !busy) {
    status = (
      <Box sx={statusSx}>
        <Typography sx={{ fontSize: 'inherit', fontWeight: 600, color: 'text.primary' }}>
          {translate('manual.search.empty', { query: trimmed })}
        </Typography>
        <Typography sx={{ fontSize: 'inherit', color: 'inherit', mt: 0.5 }}>{translate('manual.search.emptyHint')}</Typography>
      </Box>
    );
  }

  const shown = current ?? (search.data && busy ? search.data : null);
  const results = status ? [] : shown?.results ?? [];

  return (
    <Box component="section" aria-label={translate('manual.search.results')} aria-busy={busy || undefined}>
      <Box sx={{ height: 2, mt: 0.5 }}>{busy && <LinearProgress aria-hidden sx={{ height: 2 }} />}</Box>
      <Typography component="p" aria-live="polite" sx={visuallyHidden}>
        {current && !busy ? translate('manual.search.count', { smart_count: current.total }) : ''}
      </Typography>
      {status}
      {results.length > 0 && (
        <Box component="ol" sx={{ listStyle: 'none', m: 0, p: 0 }}>
          {results.map((result) => {
            const { to, state } = manualSectionTarget(result.chapterId, result.anchor);
            const terms = shown?.terms ?? [];
            return (
              <Box component="li" key={`${result.chapterId}#${result.anchor ?? ''}#${result.match}`}>
                <Box
                  component={RouterLink}
                  to={to}
                  state={state}
                  onClick={() => onOpen(result)}
                  data-manual-result
                  sx={(theme) => ({
                    display: 'block',
                    px: 1.25,
                    py: 1,
                    borderRadius: theme.custom.menu.item.borderRadius,
                    color: 'text.secondary',
                    textDecoration: 'none',
                    '&:hover': {
                      bgcolor: theme.custom.menu.item.hover.backgroundColor(theme),
                    },
                    '&:focus-visible': { outline: 'none', boxShadow: theme.custom.focusRing },
                  })}
                >
                  {result.staff && <StaffLabel sx={{ mb: 0.25 }} />}
                  <Typography component="span" sx={{ display: 'block', fontSize: '0.875rem', lineHeight: 1.4, color: 'text.primary' }}>
                    <HighlightedText text={result.chapterTitle} terms={terms} />
                    {result.heading && (
                      <>
                        <Box component="span" aria-hidden sx={{ mx: 0.5, color: 'text.disabled' }}>
                          ›
                        </Box>
                        <Box component="span" sx={visuallyHidden}>
                          {', '}
                        </Box>
                        <HighlightedText text={result.heading} terms={terms} />
                      </>
                    )}
                  </Typography>
                  {result.snippet && (
                    <Typography component="span" sx={{ display: 'block', mt: 0.25, fontSize: '0.8125rem', lineHeight: 1.45 }}>
                      <HighlightedText text={result.snippet} terms={terms} />
                    </Typography>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
      {current && !busy && current.total > current.results.length && (
        <Typography sx={statusSx}>{translate('manual.search.more', { shown: current.results.length, total: current.total })}</Typography>
      )}
    </Box>
  );
};
